import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { TZ_SHORT, formatCourseSchedule, getJoinLink } from '../../utils/courseSchedule';

const TYPE_LABELS = {
  long_course: 'Long Program',
  block: 'Short Program',
  workshop: 'Workshop'
};

const STATUS_CONFIG = {
  active: { label: 'Active', bg: 'bg-green-100', text: 'text-green-700' },
  completed: { label: 'Completed', bg: 'bg-blue-100', text: 'text-blue-700' },
  cancelled: { label: 'Cancelled', bg: 'bg-gray-100', text: 'text-gray-700' },
  payment_failed: { label: 'Payment Failed', bg: 'bg-red-100', text: 'text-red-700' },
  pending_payment: { label: 'Pending Payment', bg: 'bg-yellow-100', text: 'text-yellow-700' }
};

const LOCATION_LABELS = {
  online: 'Online',
  in_person: 'In-Person',
  hybrid: 'Hybrid'
};

const formatSchedule = (enrollment) => {
  const course = enrollment.course;
  if (!course?.schedule) return null;

  const tz = TZ_SHORT[course.schedule.timezone] || course.schedule.timezone || '';

  if (course.type === 'workshop') {
    if (course.schedule.sessionDate) {
      const d = new Date(course.schedule.sessionDate);
      const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return course.schedule.startTime ? `${dateStr} · ${course.schedule.startTime} ${tz}`.trim() : dateStr;
    }
    return null;
  }

  if (course.type === 'block') {
    const parts = [];
    if (course.schedule.blockStartDate) {
      const d = new Date(course.schedule.blockStartDate);
      parts.push(`Starts ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`);
    }
    if (course.schedule.blockWeeks) {
      parts.push(`${course.schedule.blockWeeks} weeks`);
    }
    const sched = formatCourseSchedule(course, { short: true, includeEndTime: false });
    if (sched) parts.push(sched);
    return parts.join(' · ');
  }

  // long_course
  return formatCourseSchedule(course, { short: true, includeEndTime: false }) || null;
};

const formatBilling = (enrollment) => {
  if (!enrollment.billingPlan) return null;
  const { amount, frequency, currency } = enrollment.billingPlan;
  const symbol = currency === 'USD' ? '$' : currency;
  if (frequency === 'one_time' || frequency === 'one-time') {
    return `${symbol}${amount} one-time`;
  }
  return `${symbol}${amount}/${frequency}`;
};

const MyCourses = () => {
  const navigate = useNavigate();
  const { selectedProfile } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        const { data } = await api.get('/enroll/my');
        const all = data.enrollments || [];
        const filtered = selectedProfile
          ? all.filter(e => (e.student?._id || e.student)?.toString() === selectedProfile._id.toString())
          : all;
        setEnrollments(filtered);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load your courses');
      } finally {
        setLoading(false);
      }
    };
    fetchEnrollments();
  }, [selectedProfile?._id]);

  if (loading) {
    return <div className="text-gray-500">Loading your courses...</div>;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Courses</h1>
        <button
          onClick={() => navigate('/student/scan')}
          className="inline-flex items-center gap-2 bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75H16.5v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75H16.5v-.75z" />
          </svg>
          Scan QR Code
        </button>
      </div>

      {enrollments.length === 0 ? (
        <div className="bg-white p-12 rounded-xl border border-gray-100 shadow-sm text-center">
          <div className="text-4xl mb-4 text-gray-300">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">You haven't enrolled in any courses yet</h3>
          <p className="text-gray-500">When you enroll in a course, it will appear here with your schedule, materials, and attendance info.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {enrollments.map((enrollment) => {
            const course = enrollment.course;
            if (!course) return null;
            const status = STATUS_CONFIG[enrollment.status] || STATUS_CONFIG.active;
            const schedule = formatSchedule(enrollment);
            const billing = formatBilling(enrollment);
            const joinUrl = getJoinLink(course);
            const hasClassroom = course.googleClassroom?.courseUrl;

            return (
              <div
                key={enrollment._id}
                onClick={() => navigate(`/student/course/${course._id}`)}
                className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all cursor-pointer overflow-hidden"
              >
                <div className="p-5">
                  {/* Top row: course name + status badge */}
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-semibold text-lg text-gray-900">{course.name}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text} whitespace-nowrap ml-3`}>
                      {status.label}
                    </span>
                  </div>

                  {/* Subtitle: dance style, level, type */}
                  <p className="text-sm text-gray-500 mb-3">
                    {course.danceStyle || ''}
                    {course.danceStyle && course.levelName ? ' · ' : ''}
                    {course.levelName || ''}
                    {(course.danceStyle || course.levelName) && course.type ? ' · ' : ''}
                    {TYPE_LABELS[course.type] || ''}
                  </p>

                  {/* Info row */}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-600">
                    {/* Schedule */}
                    {schedule && (
                      <span className="flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                        </svg>
                        {schedule}
                      </span>
                    )}

                    {/* Location */}
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                      </svg>
                      {LOCATION_LABELS[course.location?.type] || 'In-Person'}
                    </span>

                    {/* Attendance */}
                    <span className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {enrollment.totalClassesAttended || 0} classes attended
                    </span>

                  </div>

                  {/* Studio name */}
                  {enrollment.studio?.name && (
                    <p className="text-xs text-gray-400 mt-3">
                      {enrollment.studio.name}
                    </p>
                  )}
                </div>

                {/* Action buttons bar */}
                {(joinUrl || hasClassroom) && (
                  <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center gap-4">
                    {joinUrl && (
                      <a
                        href={joinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 bg-brand-500 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-brand-600 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
                        </svg>
                        Join class
                      </a>
                    )}
                    {hasClassroom && (
                      <a
                        href={course.googleClassroom.courseUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-sm text-brand-500 hover:text-brand-600 font-medium"
                      >
                        View in Classroom
                      </a>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/student/course/${course._id}`);
                      }}
                      className="text-sm text-gray-600 hover:text-gray-900 font-medium"
                    >
                      View Materials
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyCourses;
